// The tenancy list endpoint returns every tenancy the caller can see, so the subtree for one
// tenancy has to be picked out here. HierarchyPath would do it in a single string compare, but
// nothing backfilled that column, so it is only set on rows written since the trigger landed.
// Walking ParentTenancyId works for every row.

export function buildTenancyTree(tenancies, currentTenancyId) {
    const currentId = parseInt(currentTenancyId, 10);
    const tenancyMap = new Map();

    if (!Array.isArray(tenancies) || Number.isNaN(currentId)) {
        return { rootNode: null, tenancyMap };
    }

    const root = tenancies.find(t => t.tenancyId === currentId);
    if (!root) {
        return { rootNode: null, tenancyMap };
    }

    const childrenByParent = new Map();
    tenancies.forEach(t => {
        const siblings = childrenByParent.get(t.parentTenancyId);
        if (siblings) {
            siblings.push(t);
        } else {
            childrenByParent.set(t.parentTenancyId, [t]);
        }
    });

    const build = (tenancy) => {
        const node = { id: tenancy.tenancyId, name: tenancy.name, parentTenancyId: tenancy.parentTenancyId, children: [] };
        tenancyMap.set(node.id, node);

        // Skipping children already in tenancyMap stops a parent cycle in the data from
        // building a tree that never terminates.
        node.children = (childrenByParent.get(tenancy.tenancyId) || [])
            .filter(child => !tenancyMap.has(child.tenancyId))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map(build);

        return node;
    };

    return { rootNode: build(root), tenancyMap };
}

export function flattenVisibleNodes(rootNode, collapsed, currentTenancyId) {
    const currentId = parseInt(currentTenancyId, 10);
    const visible = [];

    if (!rootNode) {
        return visible;
    }

    const walk = (nodes, level, parentLines) => {
        nodes.forEach((node, index) => {
            const isLast = index === nodes.length - 1;

            visible.push({
                id: node.id,
                name: node.name,
                level,
                isLast,
                parentLines,
                hasChildren: node.children.length > 0,
                isCurrentTenancy: node.id === currentId
            });

            if (node.children.length > 0 && !collapsed.has(node.id)) {
                walk(node.children, level + 1, level === 0 ? [] : [...parentLines, !isLast]);
            }
        });
    };

    walk([rootNode], 0, []);
    return visible;
}
